import React, { useState } from 'react';
import { FootHealthAssessment } from '../FootHealthMonitoring';
import { ArrowRight, Calendar, Check, Clock, Download, ShoppingBag, Dumbbell, User, AlertCircle } from 'lucide-react';

interface RecommendationEngineProps {
  assessment: FootHealthAssessment;
}

interface ExtendedRecommendation {
  id: string;
  type: 'FOOTWEAR' | 'EXERCISE' | 'MEDICAL' | 'LIFESTYLE';
  title: string;
  description: string;
  urgency: 'LOW' | 'MEDIUM' | 'HIGH';
  completed: boolean;
  details: string[];
  benefits: string[];
  icon: React.ReactNode;
  schedule?: string;
  difficulty?: 'Easy' | 'Moderate' | 'Advanced';
  timeRequired?: string;
}

const RecommendationEngine: React.FC<RecommendationEngineProps> = ({ assessment }) => {
  const [filter, setFilter] = useState<'ALL' | 'FOOTWEAR' | 'EXERCISE' | 'MEDICAL' | 'LIFESTYLE'>('ALL');
  
  // Create extended recommendations with more detailed information
  const extendRecommendations = (): ExtendedRecommendation[] => {
    // Start with the recommendations from the assessment
    const baseRecommendations = assessment.recommendedActions;
    
    // Create extended versions with more details
    return baseRecommendations.map(rec => {
      const extended: ExtendedRecommendation = {
        ...rec,
        details: [],
        benefits: [],
        icon: <ArrowRight size={18} />
      };
      
      // Add type-specific details
      switch (rec.type) {
        case 'FOOTWEAR':
          extended.icon = <ShoppingBag size={18} />;
          
          if (rec.title.includes('supportive')) {
            extended.details = [
              'Look for shoes with built-in arch support',
              'Choose shoes with a cushioned heel',
              'Ensure proper width to prevent pressure points',
              'Consider brands known for supportive features'
            ];
            extended.benefits = [
              'Reduced heel pain',
              'Better weight distribution',
              'Improved walking comfort',
              'Prevention of further arch flattening'
            ];
            extended.timeRequired = 'One-time purchase, replace every 6-12 months';
          }
          break;
          
        case 'EXERCISE':
          extended.icon = <Dumbbell size={18} />;
          extended.schedule = 'Daily, 2-3 times per day';
          
          if (rec.title.includes('stretching')) {
            extended.details = [
              'Calf stretch: Stand facing a wall with one foot behind the other, lean forward keeping back heel down',
              'Plantar fascia stretch: Sit down and cross foot over opposite knee, pull toes back towards shin',
              'Towel stretch: Sit with legs extended, loop towel around forefoot and pull gently',
              'Toe curls: Place towel on floor, use toes to scrunch it towards you'
            ];
            extended.benefits = [
              'Increased flexibility in plantar fascia',
              'Reduced morning heel pain',
              'Improved foot mechanics',
              'Better blood circulation to foot tissues'
            ];
            extended.difficulty = 'Easy';
            extended.timeRequired = '5-10 minutes per session';
          }
          break;
          
        case 'MEDICAL':
          extended.icon = <User size={18} />;
          
          if (rec.title.includes('podiatrist')) {
            extended.details = [
              'Schedule an appointment with a podiatrist for professional evaluation',
              'Bring your pressure map data to the appointment',
              'Discuss custom orthotic options',
              'Ask about physical therapy referrals if needed'
            ];
            extended.benefits = [
              'Professional diagnosis of your condition',
              'Personalized treatment plan',
              'Access to prescription-strength interventions if needed',
              'Expert monitoring of your progress'
            ];
            extended.timeRequired = '1-hour initial consultation';
          }
          break;
          
        case 'LIFESTYLE':
          extended.icon = <Calendar size={18} />;
          
          if (rec.title.includes('weight')) {
            extended.details = [
              'Maintain a healthy weight to reduce foot pressure',
              'Choose low-impact exercises like swimming or cycling',
              'Gradually increase activity levels',
              'Take standing breaks if your job requires long periods of sitting'
            ];
            extended.benefits = [
              'Reduced mechanical stress on feet',
              'Improved overall mobility',
              'Better weight distribution',
              'Enhanced circulation to extremities'
            ];
            extended.schedule = 'Ongoing lifestyle adjustments';
          }
          break;
      }
      
      return extended;
    });
  };
  
  // Add additional general recommendations based on overall risk levels
  const addGeneralRecommendations = (recommendations: ExtendedRecommendation[]): ExtendedRecommendation[] => {
    const result = [...recommendations];
    
    // If no medical recommendation exists but risk is high, add one
    if (assessment.overallAssessment.plantarFasciitisRisk > 50 && 
        !recommendations.some(r => r.type === 'MEDICAL')) {
      result.push({
        id: 'gen-medical-1',
        type: 'MEDICAL',
        title: 'Consult with a foot specialist',
        description: 'Your data indicates potential foot health concerns that should be evaluated by a professional.',
        urgency: 'MEDIUM',
        completed: false,
        details: [
          'Find a podiatrist with experience in biomechanics',
          'Share your pressure mapping data during consultation',
          'Discuss preventative options before condition worsens',
          'Ask about custom orthotics or appropriate interventions'
        ],
        benefits: [
          'Early intervention to prevent worsening',
          'Professional guidance on treatment options',
          'Personalized care plan',
          'Access to specialized treatments if needed'
        ],
        icon: <User size={18} />,
        timeRequired: '1-hour consultation'
      });
    }
    
    // If no lifestyle recommendations, add one
    if (!recommendations.some(r => r.type === 'LIFESTYLE')) {
      result.push({
        id: 'gen-lifestyle-1',
        type: 'LIFESTYLE',
        title: 'Implement foot-healthy habits',
        description: 'Small daily habits can make a big difference in your foot health over time.',
        urgency: 'LOW',
        completed: false,
        details: [
          'Avoid prolonged periods of standing on hard surfaces',
          'Wear supportive shoes even at home instead of going barefoot',
          'Apply ice to sore areas for 15-20 minutes after high activity',
          'Perform regular foot self-examinations'
        ],
        benefits: [
          'Prevention of foot problems',
          'Reduced foot fatigue',
          'Better overall foot health',
          'Early detection of potential issues'
        ],
        icon: <Calendar size={18} />,
        schedule: 'Daily habits'
      });
    }
    
    return result;
  };
  
  // Process recommendations
  const allRecommendations = addGeneralRecommendations(extendRecommendations());
  
  // Apply filter if needed
  const filteredRecommendations = filter === 'ALL' 
    ? allRecommendations 
    : allRecommendations.filter(rec => rec.type === filter);
  
  // Group recommendations by urgency
  const highPriorityRecs = filteredRecommendations.filter(rec => rec.urgency === 'HIGH');
  const mediumPriorityRecs = filteredRecommendations.filter(rec => rec.urgency === 'MEDIUM');
  const lowPriorityRecs = filteredRecommendations.filter(rec => rec.urgency === 'LOW');
  
  // Toggle completion status
  const toggleCompletion = (id: string) => {
    // In a real app, this would update the backend
    console.log(`Toggling completion for recommendation: ${id}`);
  };
  
  // Generate PDF report - placeholder function
  const generateReport = () => {
    console.log('Generating PDF report');
    // In a real implementation, this would generate a PDF report
  };
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium">Personalized Recommendations</h3>
        <div className="flex space-x-3">
          <div className="flex items-center text-sm">
            <button
              onClick={() => setFilter('ALL')}
              className={`px-3 py-1 rounded-l-md ${
                filter === 'ALL' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('FOOTWEAR')}
              className={`px-3 py-1 ${
                filter === 'FOOTWEAR' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Footwear
            </button>
            <button
              onClick={() => setFilter('EXERCISE')}
              className={`px-3 py-1 ${
                filter === 'EXERCISE' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Exercises
            </button>
            <button
              onClick={() => setFilter('MEDICAL')}
              className={`px-3 py-1 ${
                filter === 'MEDICAL' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Medical
            </button>
            <button
              onClick={() => setFilter('LIFESTYLE')}
              className={`px-3 py-1 rounded-r-md ${
                filter === 'LIFESTYLE' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Lifestyle
            </button>
          </div>
          
          <button
            onClick={generateReport}
            className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-sm hover:bg-gray-50"
          >
            <Download size={16} className="mr-2" />
            Save Plan
          </button>
        </div>
      </div>
      
      {/* Action summary */}
      <div className="bg-white rounded-lg shadow mb-6 p-4 border-l-4 border-blue-500">
        <h4 className="font-medium mb-2">Recommendation Summary</h4>
        <p className="text-sm text-gray-700">
          Based on your foot pressure analysis, we've identified {allRecommendations.length} recommendations 
          to help improve your foot health and prevent future problems.
          {assessment.overallAssessment.plantarFasciitisRisk > 50 && 
            " Your data shows significant indicators of developing plantar fasciitis. Following these recommendations is important to prevent progression."}
        </p>
        
        <div className="mt-3 flex flex-wrap gap-3">
          <div className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium flex items-center">
            <AlertCircle size={14} className="mr-1" />
            {highPriorityRecs.length} High Priority
          </div>
          <div className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium flex items-center">
            <Clock size={14} className="mr-1" />
            {mediumPriorityRecs.length} Medium Priority
          </div>
          <div className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium flex items-center">
            <Check size={14} className="mr-1" />
            {lowPriorityRecs.length} Preventative
          </div>
        </div>
      </div>
      
      {/* High priority section */}
      {highPriorityRecs.length > 0 && (
        <div className="mb-6">
          <h4 className="text-red-600 font-medium mb-3 flex items-center">
            <AlertCircle size={18} className="mr-2" />
            High Priority Actions
          </h4>
          
          <div className="space-y-4">
            {highPriorityRecs.map(rec => (
              <div 
                key={rec.id}
                className="bg-white rounded-lg shadow overflow-hidden border-l-4 border-red-500"
              >
                <div className="p-4">
                  <div className="flex items-start">
                    <div className="mr-3 h-6 w-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
                      {rec.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <h5 className="font-medium">{rec.title}</h5>
                        <button
                          onClick={() => toggleCompletion(rec.id)}
                          className={`h-6 px-2 text-xs font-medium rounded-full flex items-center ${
                            rec.completed 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {rec.completed ? (
                            <>
                              <Check size={12} className="mr-1" />
                              Completed
                            </>
                          ) : 'Mark Complete'}
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{rec.description}</p>
                      
                      {rec.timeRequired && (
                        <div className="mt-2 flex items-center text-xs text-gray-500">
                          <Clock size={12} className="mr-1" />
                          <span>{rec.timeRequired}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-3 pl-9">
                    <div className="flex flex-wrap gap-2 mt-2">
                      <div className="px-2 py-0.5 bg-red-50 text-red-600 rounded text-xs">
                        Urgent
                      </div>
                      <div className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                        {rec.type}
                      </div>
                    </div>
                    
                    {rec.details.length > 0 && (
                      <div className="mt-3">
                        <h6 className="text-xs font-medium text-gray-500 mb-1">ACTION STEPS:</h6>
                        <ul className="text-sm space-y-1">
                          {rec.details.map((detail, idx) => (
                            <li key={idx} className="flex items-start">
                              <span className="text-red-500 mr-2">•</span>
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Medium priority section */}
      {mediumPriorityRecs.length > 0 && (
        <div className="mb-6">
          <h4 className="text-yellow-600 font-medium mb-3 flex items-center">
            <Clock size={18} className="mr-2" />
            Medium Priority Actions
          </h4>
          
          <div className="space-y-4">
            {mediumPriorityRecs.map(rec => (
              <div 
                key={rec.id}
                className="bg-white rounded-lg shadow overflow-hidden border-l-4 border-yellow-500"
              >
                <div className="p-4">
                  <div className="flex items-start">
                    <div className="mr-3 h-6 w-6 rounded-full bg-yellow-100 text-yellow-600 flex items-center justify-center flex-shrink-0">
                      {rec.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <h5 className="font-medium">{rec.title}</h5>
                        <button
                          onClick={() => toggleCompletion(rec.id)}
                          className={`h-6 px-2 text-xs font-medium rounded-full flex items-center ${
                            rec.completed 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {rec.completed ? (
                            <>
                              <Check size={12} className="mr-1" />
                              Completed
                            </>
                          ) : 'Mark Complete'}
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{rec.description}</p>
                      
                      {(rec.timeRequired || rec.schedule) && (
                        <div className="mt-2 flex items-center text-xs text-gray-500">
                          {rec.timeRequired && (
                            <>
                              <Clock size={12} className="mr-1" />
                              <span className="mr-3">{rec.timeRequired}</span>
                            </>
                          )}
                          {rec.schedule && (
                            <>
                              <Calendar size={12} className="mr-1" />
                              <span>{rec.schedule}</span>
                            </>
                          )}
                        </div>
                      )}
                      
                      {rec.difficulty && (
                        <div className="mt-1 text-xs text-gray-500">
                          <span className="font-medium">Difficulty:</span> {rec.difficulty}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-3 pl-9">
                    <div className="flex flex-wrap gap-2">
                      <div className="px-2 py-0.5 bg-yellow-50 text-yellow-600 rounded text-xs">
                        Medium Priority
                      </div>
                      <div className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                        {rec.type}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                      {rec.details.length > 0 && (
                        <div>
                          <h6 className="text-xs font-medium text-gray-500 mb-1">ACTION STEPS:</h6>
                          <ul className="text-sm space-y-1">
                            {rec.details.map((detail, idx) => (
                              <li key={idx} className="flex items-start">
                                <span className="text-yellow-500 mr-2">•</span>
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {rec.benefits.length > 0 && (
                        <div>
                          <h6 className="text-xs font-medium text-gray-500 mb-1">BENEFITS:</h6>
                          <ul className="text-sm space-y-1">
                            {rec.benefits.map((benefit, idx) => (
                              <li key={idx} className="flex items-start">
                                <span className="text-green-500 mr-2">•</span>
                                <span>{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Low priority section */}
      {lowPriorityRecs.length > 0 && (
        <div>
          <h4 className="text-green-600 font-medium mb-3 flex items-center">
            <Check size={18} className="mr-2" />
            Preventative Actions
          </h4>
          
          <div className="space-y-4">
            {lowPriorityRecs.map(rec => (
              <div 
                key={rec.id}
                className="bg-white rounded-lg shadow overflow-hidden border-l-4 border-green-500"
              >
                <div className="p-4">
                  <div className="flex items-start">
                    <div className="mr-3 h-6 w-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0">
                      {rec.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <h5 className="font-medium">{rec.title}</h5>
                        <button
                          onClick={() => toggleCompletion(rec.id)}
                          className={`h-6 px-2 text-xs font-medium rounded-full flex items-center ${
                            rec.completed 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {rec.completed ? (
                            <>
                              <Check size={12} className="mr-1" />
                              Completed
                            </>
                          ) : 'Mark Complete'}
                        </button>
                      </div>
                      
                      <p className="text-sm text-gray-600 mt-1">{rec.description}</p>
                      
                      {(rec.timeRequired || rec.schedule) && (
                        <div className="mt-2 flex items-center text-xs text-gray-500">
                          {rec.timeRequired && (
                            <>
                              <Clock size={12} className="mr-1" />
                              <span className="mr-3">{rec.timeRequired}</span>
                            </>
                          )}
                          {rec.schedule && (
                            <>
                              <Calendar size={12} className="mr-1" />
                              <span>{rec.schedule}</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-3 pl-9">
                    <div className="flex flex-wrap gap-2">
                      <div className="px-2 py-0.5 bg-green-50 text-green-600 rounded text-xs">
                        Preventative
                      </div>
                      <div className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                        {rec.type}
                      </div>
                    </div>
                    
                    {rec.details.length > 0 && (
                      <div className="mt-3">
                        <h6 className="text-xs font-medium text-gray-500 mb-1">RECOMMENDED ACTIONS:</h6>
                        <ul className="text-sm space-y-1">
                          {rec.details.map((detail, idx) => (
                            <li key={idx} className="flex items-start">
                              <span className="text-green-500 mr-2">•</span>
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Empty state */}
      {filteredRecommendations.length === 0 && (
        <div className="bg-white rounded-lg p-6 text-center">
          <div className="flex justify-center mb-3">
            <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
              <Check size={24} className="text-gray-400" />
            </div>
          </div>
          <h4 className="font-medium text-gray-900 mb-1">No Recommendations Found</h4>
          <p className="text-gray-500 mb-4">There are no recommendations matching your current filter.</p>
          <button
            onClick={() => setFilter('ALL')}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            View All Recommendations
          </button>
        </div>
      )}
    </div>
  );
};

export default RecommendationEngine; 